import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Grid,
  Checkbox,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useAppSelector } from "src/app/hooks";
import { OtherPrivilegeGuard } from "src/utils/authorization/OtherPrivilegeGuard";
import { useCardForm } from "src/hooks/useCardForm";
import { useTranslate } from "src/locales/use-locales";
import ByzCard from "src/components/core-components/ByzCard";
import ByzTitleBox from "src/components/core-components/ByzTitleBox";
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import { GridColDef, GridDeleteIcon } from "@mui/x-data-grid-premium";
import PatientDatas from "src/modules/medcard/patient-datas";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import PainAssessmentScaleInfoGrid from "../components/PainAssessmentScaleInfoGrid";
import ByzMcParameterDetailSelect from "src/components/shared-components/ByzMcParameterDetailSelect";
import { McParameterDetailResponseDto } from "src/api/references/referenceServicesDtos";
import PainScaleCard from "../components/PainScaleCard";
import PainLocationCard from "../components/PainLocationCard";
import PainTypeCard from "../components/PainTypeCard";
import NewBornScaleNips from "../components/scales/NewBornScaleNips";
import FlaccScale from "../components/scales/FlaccScale";
import VisualAnalogScale from "../components/scales/VizuelAnalogScale";
import DasScale from "../components/scales/DasScale";
import PippScale from "../components/scales/PippScale";
import WongBakerScale from "../components/scales/WongBakerScale";
import ByzMuiAddButton from "src/components/core-components/mui/buttons/ByzMuiAddButton";
import ByzDeleteConfirmDialog from "src/components/core-components/mui/ByzDeleteConfirmDialog";
import ByzMuiButton from "src/components/core-components/mui/buttons/ByzMuiButton";
import { Add } from "@mui/icons-material";
import NurseApplication from "../components/NurseApplication";
import { referenceService } from "src/api/references/referenceService";
import { getCardData } from "src/utils/card/cardData";
import { getLocalISO } from "src/utils/date/dateHelper";

export interface PainAssmentScaleFormData {
  PAS_ID?: string;
  CTIME?: Date;
  CUSER?: string;
  PAIN_SCALE?: number;
  PLACE_OF_PAIN?: number;
  TYPE_AND_QUALITIES?: number;
  ACCOMPANYING_SYMPTOMS?: number;
  BEGIN_TIME?: Date;
  TIME?: string;
  FREQUENCY?: number;
  DAILY_LIFE_EFFECTS?: number;
  SPREAD_OF_PAIN?: string;
  NIPS_FACE_EXPRESSION?: number;
  NIPS_CRY?: number;
  NIPS_RESPIRATORY_FORM?: number;
  NIPS_ARMS?: number;
  NIPS_LEGS?: number;
  NIPS_WAKEFULLNESS?: number;
  FLACC_FACE_EXPRESSION?: number;
  FLACC_LEGS?: number;
  FLACC_ACTIVITE?: number;
  FLACC_CRY?: number;
  FLACC_CONSOLATION?: number;
  DAS_FACE_EXPRESSION?: number;
  DAS_UPPER_EXTREMITY_MOV?: number;
  DAS_ADAPTATION_VEN?: number;
  NIPS_SCALE_SCORE?: number;
  FLACC_SCALE_SCORE?: number;
  WONG_BAKER_SCALE_SCORE?: number;
  VAS_SCALE_SCORE?: number;
  DAS_SCALE_SCORE?: number;
  PROCESS_TIME?: Date;
  INCREASING_FACTORS?: number;
  INCREASING_FACTORS_EXPLANATION?: string;
  REDUCING_FACTORS?: number;
  REDUCING_FACTORS_EXPLANATION?: string;
  PIPP_GESTATIONAL_AGE?: number;
  PIPP_BEHAVIORAL_STATE?: number;
  PIPP_MAX_HEART_RATE?: number;
  PIPP_MIN_OXYGEN_SATURATION?: number;
  PIPP_BROW_BULGE?: number;
  PIPP_EYE_SQUEEZE?: number;
  PIPP_NASOLABIAL_FURROW?: number;
  PIPP_SCALE_SCORE?: number;
  EVALUATION_REASON?: number;
  SIDE?: number;
  TARGET_EXPLANATION?: string;
  PLACE_PAIN_EXPLANATION?: string;
  TYPE_QUALITIES_EXPLANATION?: string;
  NURSE_NOTE?: string;
}

interface PainAssmentScaleScoreProps {
  recordId?: string;
  patientId: number;
  onClose?: () => void;
  getComId: () => Promise<number | null>;
  comId: number;
  setComId: (comId: number) => void;
  getCardTableData: (comId: number) => Promise<string | "">;
  createTableMasterId: () => Promise<string | null>;
  save: (id?: string) => Promise<void | string | null>;
  handleChange: (field: keyof PainAssmentScaleFormData, value: any) => void;
  patientData: any;
  handleDeleteClick: (rowId: number) => void;
  handleDeleteConfirm: () => Promise<boolean>;
  handleDeleteCancel: () => void;
  deleteLoading: number | null;
  setDeleteLoading: (id: number | null) => void;
  rows: any;
  getCardTableDataWithTableId: (
    tableMasterId: number
  ) => Promise<Record<string, any>[]>;
  setRows: any;
  setEditItemId: any;
  setDeleteItemId: any;
  extraAccess?: boolean;
}

function PainAssesmentScaleScoreCreate({
  recordId,
  save,
  handleChange: parentHandleChange,
  handleDeleteClick,
  handleDeleteConfirm,
  handleDeleteCancel,
  deleteLoading,
  rows,
  setRows,
  getCardTableDataWithTableId,
  setEditItemId,
  setDeleteItemId,
  extraAccess,
}: PainAssmentScaleScoreProps) {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => state.auth);
  const [insertLoading, setInsertLoading] = useState(false);

  const {
    formData,
    loading,
    selectedMCPs,
    handleChange,
    handleMCPChange,
    resetForm,
    cardData,
    isEditMode,
    getMCPParameterDetails,
    mcpCacheLoaded,
  } = useCardForm({
    cardType: "PAIN_ASSESSMENT_SCALE",
    recordId,
    user,
    onLoadRecord: (record) => {
      Object.entries(record).forEach(([key, value]) => {
        parentHandleChange(key as keyof PainAssmentScaleFormData, value);
      });
    },
  });

  const typedFormData = formData as PainAssmentScaleFormData;

  useEffect(() => {
    if (!isEditMode) resetForm();
  }, [isEditMode, resetForm]);

  useEffect(() => {
    Object.entries(formData || {}).forEach(([key, value]) => {
      parentHandleChange(key as keyof PainAssmentScaleFormData, value);
    });
  }, [formData, parentHandleChange]);

  const [selectedPainScaleIndex, setSelectedPainScaleIndex] = useState<
    number | null
  >(99);
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const [nurseRows, setNurseRows] = useState<Record<string, any>[]>([]);

  const handleGetNurseApplicationRows = async (id: number) => {
    try {
      const cardDataNurseApp = getCardData("NURSE_APPLICATION");
      const mcpdNameList = Object.keys(cardDataNurseApp?.mcpConfig || {}).join(
        ","
      );
      if (id <= 0) return;

      const response = await referenceService.card.getCardTableData({
        tableName: "MC_PAIN_ASS_SCORE_NURSE_APP",
        columnName: "MCPASD_ID",
        columnValue: id,
        mcpdNameList,
      });

      const formattedRows = (response.data ?? []).map(
        (item: any, index: number) => ({
          id: item.ID ?? `row-${index}`,
          ID: item.ID,
          CTIME: item.CTIME,
          CHOUR: item.CHOUR.split("T")[1].substring(0, 5),
          NON_MEDICAL_APP:
            item.NON_MEDICAL_APP_XNAME ??
            t("pain-assessment-scale.nurse-application.not-selected"),
          ATTEMPTS_MADE: item.ATTEMPTS_MADE_XNAME,
          SIDE_EFFECTS:
            item.SIDE_EFFECTS_XNAME ??
            t("pain-assessment-scale.nurse-application.not-selected"),
          ACTIVITY_CONTROL:
            item.ACTIVITY_CONTROL_XNAME ??
            t("pain-assessment-scale.nurse-application.not-selected"),
          PAIN_SCORE: item.PAIN_SCORE,
          NURSE_EXPLANATION: item.NURSE_EXPLANATION,
          CUSER: item.CUSER,
        })
      );

      setNurseRows(formattedRows);
    } catch (error) {
      console.error("getCardTableData error:", error);
    }
  };

  const handleDeleteClickNurseApplications = async (rowId: number) => {
    try {
      await referenceService.card.delete({
        tablename: "MC_PAIN_ASS_SCORE_NURSE_APP",
        id: rowId,
      });

      if (selectedRowId) {
        handleGetNurseApplicationRows(selectedRowId);
      }
    } catch (error) {
      console.error("handleDeleteClick error:", error);
    }
  };

  useEffect(() => {
    if (selectedRowId) {
      handleGetNurseApplicationRows(selectedRowId);
    } else {
      setNurseRows([]);
    }
  }, [selectedRowId]);

  const handleEditClick = async (rowId: number) => {
    try {
      const response = await getCardTableDataWithTableId(rowId);
      if (!response.length) return;

      const rowData = response[0];

      setSelectedRowId(rowId);
      setEditItemId(rowId);

      Object.entries(rowData).forEach(([key, value]) => {
        if (key in typedFormData) {
          handleChange(key as keyof PainAssmentScaleFormData, value);
        }

        if (cardData?.mcpConfig?.[key]) {
          const mcpConfig = cardData.mcpConfig[key];
          if (!mcpConfig) return;

          if (!value || value <= 0) {
            handleMCPChange(key, null);
            return;
          }

          const selected: McParameterDetailResponseDto = {
            id: value.id ?? value,
            value: value.value ?? 0,
            mcpId: mcpConfig.mcpId,
            name: value.name ?? "",
            note: value.note ?? "",
            orderNumber: value.orderNumber ?? 0,
          };

          handleMCPChange(key, selected);
        }
      });
    } catch (e) {
      console.error("Edit load failed:", e);
    }
  };

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const handleDeleteClickwithConfirm = async (rowId: number) => {
    setOpenDeleteDialog(false);
    setSelectedRowId(rowId);
    setDeleteItemId(rowId);
    setOpenDeleteDialog(true);
  };

  const columns: GridColDef[] = [
    {
      field: "CHECKED",
      headerName: t("pain-assessment-scale.columns.checked"),
      flex: 1,
      renderCell(params) {
        return (
          <Checkbox
            checked={selectedRowId === params.row.ID}
            onClick={() => handleEditClick(params.row.ID)}
          />
        );
      },
    },
    {
      field: "PAIN_SCALE_XNAME",
      headerName: t("pain-assessment-scale.columns.pain-scale-type"),
      flex: 1,
    },
    {
      field: "PAIN_SCORE",
      headerName: t("pain-assessment-scale.columns.pain"),
      flex: 1,
    },
    { field: "PROCESS_TIME", headerName: t("process-time"), flex: 1 },
    { field: "CUSER", headerName: t("cuser"), flex: 1 },
    {
      field: "delete",
      headerName: t("card.delete"),
      flex: 1,
      renderCell: (params) => (
        <IconButton
          onClick={() => handleDeleteClickwithConfirm(params.row.ID)}
          disabled={deleteLoading === params.row.id}
        >
          {deleteLoading === params.row.id ? (
            <CircularProgress size={20} color="error" />
          ) : (
            <GridDeleteIcon color="error" />
          )}
        </IconButton>
      ),
    },
  ];

  const painScaleMap: Record<number, React.ElementType> = {
    0: NewBornScaleNips,
    1: FlaccScale,
    2: WongBakerScale,
    3: VisualAnalogScale,
    4: DasScale,
    5: PippScale,
  };

  const SelectedPainScale =
    selectedPainScaleIndex !== null
      ? painScaleMap[selectedPainScaleIndex]
      : null;

  const [saveLoading, setSaveLoading] = useState(false);

  const saveorInsertRecord = async (Id?: string) => {
    try {
      setSaveLoading(true);

      if (Id && Id.length > 0) {
        const newId = await save(typedFormData.PAS_ID);

        if (newId) {
          setEditItemId(Number(newId));
        } else {
          setEditItemId(null);
          console.log("Kayıt edilecek Id Bulunamadı.");
        }
      } else {
        const newId = await save();

        if (newId) {
          setEditItemId(Number(newId));
        } else {
          setEditItemId(null);
          console.log("Kayıt edilecek Id Bulunamadı.");
        }
      }
    } catch (e) {
      console.error("saveorInsertRecord error:", e);
    } finally {
      setSaveLoading(false);
    }
  };

  useEffect(() => {
    if (rows && rows.length > 0) {
      const lastRow = rows[rows.length - 1];
      if (lastRow?.ID) {
        setSelectedRowId(lastRow.ID);
        handleEditClick(lastRow.ID);
      }
    }
  }, [rows]);

  const insertNewNurseApplication = async (detailId: number) => {
    try {
      setInsertLoading(true); // <-- LOADING BAŞLA

      if (!detailId || detailId <= 0) {
        console.error("Geçersiz MCPASD_ID!");
        setInsertLoading(false);
        return;
      }
      const now = getLocalISO();
      const response = await referenceService.card.post({
        tablename: "MC_PAIN_ASS_SCORE_NURSE_APP",
        columns: [
          { key: "MCPASD_ID", value: detailId.toString() },
          { key: "CTIME", value: now },
          { key: "CUSER", value: user?.username ?? "" },
          { key: "CDATE", value: now },
          { key: "CHOUR", value: now },
        ],
      });

      if (!response) {
        console.error("Yeni Nurse Application oluşturulamadı!");
        setInsertLoading(false);
        return;
      }

      setSelectedRowId(detailId);
      await handleGetNurseApplicationRows(detailId);
    } catch (error) {
      console.error("insertNewNurseApplication error:", error);
    } finally {
      setInsertLoading(false);
    }
  };


  return (
    <>
      <ByzDeleteConfirmDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onDelete={async () => {
          handleEditClick(selectedRowId as number);
          handleDeleteClick(selectedRowId as number);
          const result = await handleDeleteConfirm();

          if (result) {
            setRows((prev: any) =>
              prev.filter((row: any) => row.ID !== selectedRowId)
            );
            if (rows.length === 1) {
              setSelectedRowId(null);
            }
          }

          return result;
        }}
        content={t("delete-confirm.title")}
      />

      <Box p={2} width="100%">
        <PatientDatas isCreateMedCard isOpenMedCard />

        <Box mb={3} mt={2} display="flex" gap={2} justifyContent="flex-start">
          <OtherPrivilegeGuard
            extraAccess={!!extraAccess}
            fallback={
              <ByzMuiSaveButton
                disabled
                tooltip={!extraAccess ? t("no-permission-for-this-action") : undefined}
              />
            }
          >
            <ByzMuiSaveButton
              onClick={() => {
                if (!selectedRowId) return;
                saveorInsertRecord(selectedRowId.toString());
              }}
              disabled={!selectedRowId || saveLoading}
            />
          </OtherPrivilegeGuard>

          <ByzMuiAddButton
            onClick={async () => {
              saveorInsertRecord();
            }}
          />

          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderColor: "rgba(0, 0, 0, 0.5)" }}
          />

          <ByzMuiButton
            onClick={() => insertNewNurseApplication(selectedRowId as number)}
            icon={insertLoading ? <CircularProgress size={18} /> : <Add />}
            disabled={insertLoading || !selectedRowId}
          >
            {insertLoading
              ? t("adding")
              : t("pain-assessment-scale.nurse-application.add")}
          </ByzMuiButton>
        </Box>

        <Divider sx={{ mb: 3, borderColor: "#000" }} />

        <ByzCard>
          <ByzTitleBox title={t("pain-assessment-scale.title")} />
          <Box p={2}>
            <ByzMuiDataGrid columns={columns} pageSize={10} rows={rows} />
          </Box>
          <PainAssessmentScaleInfoGrid />
        </ByzCard>

        <ByzCard>
          <ByzTitleBox
            title={t("pain-assessment-scale.pain-assessment.title")}
          />
          <ByzCard sx={{ mt: 2, mb: 0 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={4}>
                    <ByzMuiTypography>
                      {t(
                        "pain-assessment-scale.pain-assessment.left.pain-scale"
                      )}
                    </ByzMuiTypography>
                  </Grid>
                  <Grid item xs={8}>
                    <Box marginRight={3}>
                      <ByzMcParameterDetailSelect
                        title={t("select")}
                        mcpId={cardData.mcpConfig?.PAIN_SCALE?.mcpId ?? 0}
                        multiple={false}
                        handleChange={(val) => {
                          if (val && !Array.isArray(val)) {
                            const selected =
                              val as McParameterDetailResponseDto;
                            handleMCPChange("PAIN_SCALE", selected);
                            const options =
                              getMCPParameterDetails(
                                cardData.mcpConfig?.PAIN_SCALE?.mcpId ?? 0
                              ) || [];
                            const selectedIndex = options.findIndex(
                              (option) => option.id === selected.id
                            );
                            setSelectedPainScaleIndex(selectedIndex);
                          }
                        }}
                        value={selectedMCPs.PAIN_SCALE}
                        sx={{ marginRight: "10" }}
                        cachedData={getMCPParameterDetails(
                          cardData.mcpConfig?.PAIN_SCALE?.mcpId ?? 0
                        )}
                        cacheLoaded={mcpCacheLoaded}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </ByzCard>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <PainScaleCard
                {...{
                  handleMCPChange,
                  handleChange,
                  cardData,
                  typedFormData,
                  getMCPParameterDetails,
                  mcpCacheLoaded,
                  t,
                  selectedMCPs,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <PainLocationCard
                {...{
                  handleMCPChange,
                  handleChange,
                  cardData,
                  typedFormData,
                  getMCPParameterDetails,
                  mcpCacheLoaded,
                  t,
                  selectedMCPs,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <PainTypeCard
                {...{
                  handleMCPChange,
                  handleChange,
                  cardData,
                  typedFormData,
                  getMCPParameterDetails,
                  mcpCacheLoaded,
                  t,
                  selectedMCPs,
                }}
              />
            </Grid>
          </Grid>
        </ByzCard>

        {SelectedPainScale && (
          <SelectedPainScale
            t={t}
            cardData={cardData}
            selectedMCPs={selectedMCPs}
            typedFormData={typedFormData}
            handleMCPChange={handleMCPChange}
            getMCPParameterDetails={getMCPParameterDetails}
            mcpCacheLoaded={mcpCacheLoaded}
            handleChange={handleChange}
          />
        )}

        <NurseApplication
          rows={nurseRows}
          handleDeleteClickNurseApplications={
            handleDeleteClickNurseApplications
          }
          handleChange={handleChange}
          save={save}
          onRefresh={() =>
            handleGetNurseApplicationRows(selectedRowId as number)
          }
        />
      </Box>
    </>
  );
}

export default PainAssesmentScaleScoreCreate;
